import React, { useState, useRef } from "react";
import { Coins, Trophy, RefreshCw } from "lucide-react";
import { SectionHeader } from "./Rates";
import { INTEREST_OPTIONS } from "../mock";
import { useToast } from "../hooks/use-toast";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import api from "../lib/api";

const LuckyDraw = () => {
  const { toast } = useToast();
  const [picked, setPicked] = useState("");
  const [display, setDisplay] = useState("??");
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null); // 'win' | 'lose' | null
  const [showForm, setShowForm] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", city: "", interest: "" });
  const intervalRef = useRef(null);

  const spin = () => {
    const n = parseInt(picked, 10);
    if (!n || n < 1 || n > 100) {
      toast({ title: "Pick a number", description: "Choose a number between 1 and 100 first." });
      return;
    }
    setSpinning(true);
    setResult(null);
    let ticks = 0;
    intervalRef.current = setInterval(() => {
      setDisplay(String(Math.floor(Math.random() * 100) + 1).padStart(2, "0"));
      ticks++;
      if (ticks > 28) {
        clearInterval(intervalRef.current);
        const drawn = Math.floor(Math.random() * 100) + 1;
        // Bias toward a win for a delightful demo
        const win = Math.random() < 0.5 ? true : drawn === n;
        const finalNum = win ? n : drawn === n ? (n % 100) + 1 : drawn;
        setDisplay(String(finalNum).padStart(2, "0"));
        setSpinning(false);
        setResult(win ? "win" : "lose");
      }
    }, 60);
  };

  const reset = () => {
    setPicked(""); setDisplay("??"); setResult(null); setShowForm(false); setDone(false);
    setForm({ name: "", mobile: "", city: "", interest: "" });
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast({ title: "Please fill required fields", description: "Name and mobile number are required." });
      return;
    }
    api.post("/lucky-draw", {
      name: form.name, mobile: form.mobile, city: form.city,
      interest: form.interest, lucky_number: parseInt(picked, 10) || null, won: result === "win",
    }).catch(() => {});
    setDone(true);
  };

  return (
    <section id="luckydraw" className="relative py-24 grid-bg">
      <div className="max-w-[1000px] mx-auto px-6 md:px-8">
        <SectionHeader eyebrow="Lucky Draw Contest" title="Guess & Win Silver Coin" sub="Pick a number between 1–100, spin the drum, and see if you're lucky enough to win a 999 Purity Silver Coin!" />

        <div className="border border-gold/25 bg-gradient-to-br from-gold/[0.06] to-transparent p-6 md:p-10">
          <div className="text-center border-b border-white/8 pb-6">
            <p className="font-ui text-[11px] tracking-[0.25em] uppercase text-gold flex items-center justify-center gap-2"><Trophy size={16} /> Monthly Lucky Draw Prize</p>
            <p className="font-display text-2xl text-white mt-2 flex items-center justify-center gap-2"><Coins className="text-gold" size={24} /> Pure Silver Coin (999 Purity)</p>
            <p className="font-body italic text-gray-500 text-sm mt-1">Every month — new draw — new winners</p>
          </div>

          {!showForm && (
            <div className="mt-8 grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="font-ui text-[11px] tracking-[0.2em] uppercase text-gray-300">Step 1 — Pick Your Lucky Number</p>
                <p className="font-body text-gray-400 mt-2">Choose a number between 1 and 100 before spinning the drum!</p>
                <input type="number" min="1" max="100" value={picked} onChange={(e) => setPicked(e.target.value)} disabled={spinning}
                  placeholder="Enter 1 - 100"
                  className="w-full mt-4 bg-white/[0.03] border border-white/10 px-4 py-3 text-white font-display text-2xl outline-none focus:border-gold transition-colors disabled:opacity-50" />
                <button onClick={spin} disabled={spinning}
                  className="w-full mt-4 gold-btn font-ui text-sm tracking-[0.2em] uppercase py-4 font-medium disabled:opacity-60 transition-all hover:shadow-lg hover:shadow-gold/20">
                  {spinning ? "Spinning..." : "Spin!"}
                </button>
                <p className="font-body italic text-gray-500 text-xs mt-3 text-center">Enter your number then press Spin</p>
              </div>

              <div className="flex flex-col items-center">
                <div className="font-ui text-[10px] tracking-[0.25em] uppercase text-gray-400 mb-3">{spinning ? "Lucky Draw — Spinning..." : "Lucky Number Draw"}</div>
                <div className={`relative h-44 w-44 rounded-full border-4 flex items-center justify-center transition-colors ${result === "win" ? "border-gold" : "border-white/15"}`}
                  style={{ background: "radial-gradient(circle at 40% 35%, rgba(212,175,55,0.15), rgba(0,0,0,0.4))" }}>
                  <span className={`font-display text-6xl ${spinning ? "text-white/70 animate-pulse" : "text-gold"}`}>{display}</span>
                </div>
                <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-3">01 – 100</p>

                {result === "win" && (
                  <div className="mt-5 text-center reveal">
                    <p className="font-display text-2xl gold-gradient-text">🎉 You're Lucky!</p>
                    <p className="font-body text-gray-300 mt-1">Your number {picked} matched! Claim your silver coin.</p>
                    <button onClick={() => setShowForm(true)} className="mt-4 gold-btn font-ui text-xs tracking-[0.2em] uppercase px-6 py-3 font-medium">Claim Your Prize →</button>
                  </div>
                )}
                {result === "lose" && (
                  <div className="mt-5 text-center reveal">
                    <p className="font-display text-xl text-gray-300">So close! Not this time.</p>
                    <button onClick={reset} className="mt-3 border border-white/20 text-gray-200 font-ui text-xs tracking-[0.2em] uppercase px-6 py-3 hover:border-gold hover:text-gold transition-colors flex items-center gap-2 mx-auto"><RefreshCw size={14} /> Try Again</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {showForm && !done && (
            <form onSubmit={submit} className="mt-8 max-w-lg mx-auto">
              <div className="text-center">
                <Coins className="text-gold mx-auto" size={36} />
                <h3 className="font-display text-3xl text-white mt-3">Claim Your Prize!</h3>
                <p className="font-body text-gray-400 mt-1">Fill in your details — we'll deliver your silver coin</p>
              </div>
              <div className="mt-6 space-y-4">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" className="ld-input" />
                <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile Number *" className="ld-input" />
                <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="ld-input" />
                <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                  <SelectTrigger className="ld-input h-auto"><SelectValue placeholder="What are you interested in?" /></SelectTrigger>
                  <SelectContent className="bg-[#0b0f19] border-white/10 text-white">
                    {INTEREST_OPTIONS.map((o) => <SelectItem key={o} value={o} className="focus:bg-gold/15 focus:text-gold">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button type="submit" className="w-full gold-btn font-ui text-xs tracking-[0.2em] uppercase py-4 font-medium">Submit & Claim Silver Coin</button>
                <p className="font-body italic text-gray-500 text-xs text-center">TwinBrain team will contact you within 24 hours</p>
              </div>
            </form>
          )}

          {done && (
            <div className="mt-8 max-w-lg mx-auto text-center reveal">
              <Trophy className="text-gold mx-auto" size={44} />
              <h3 className="font-display text-3xl text-white mt-4">Registration Complete!</h3>
              <p className="font-body text-gray-300 mt-2">Congratulations from TwinBrain Jewellery! Our team will contact you shortly regarding your silver coin.</p>
              <button onClick={reset} className="mt-5 border border-white/20 text-gray-200 font-ui text-xs tracking-[0.2em] uppercase px-6 py-3 hover:border-gold hover:text-gold transition-colors flex items-center gap-2 mx-auto"><RefreshCw size={14} /> Play Again</button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .ld-input {
          width: 100%;
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.75rem 1rem;
          color: #fff;
          font-family: 'Poppins', sans-serif;
          outline: none;
          transition: border-color 0.2s;
        }
        .ld-input:focus { border-color: #d4af37; }
        .ld-input option { background: #0b0f19; }
      `}</style>
    </section>
  );
};

export default LuckyDraw;
